<template>
    <van-popup
        v-model="visible"
        position="bottom"
        round
        closeable
        safe-area-inset-bottom
        :close-on-click-overlay="false"
        @closed="reset"
    >
        <section class="mobile-account-panel">
            <template v-if="action === 'nickname'">
                <header><span>PROFILE NAME</span><h2>修改昵称</h2><p>修改后，新昵称将同步显示在个人中心。</p></header>
                <van-form :show-error-message="false" @submit="submitNickname" @failed="handleFormFailed">
                    <van-field :value="userData.nickname || '未设置'" label="当前昵称" readonly />
                    <van-field v-model.trim="nicknameForm.nickname" label="新昵称" placeholder="请输入新昵称" clearable :rules="[{required: true, message: '请输入新昵称'}]" />
                    <van-field v-model="nicknameForm.phone_verify" type="tel" maxlength="4" label="短信验证码" placeholder="请输入 4 位验证码" clearable :rules="codeRules">
                        <template #button><van-button size="small" type="info" native-type="button" :disabled="profileCountdown > 0" :loading="codeSending" @click="sendProfileCode">{{ profileCountdown > 0 ? profileCountdown + 's' : '发送验证码' }}</van-button></template>
                    </van-field>
                    <div class="mobile-account-submit"><van-button block round type="info" native-type="submit" :loading="submitting">保存昵称</van-button></div>
                </van-form>
            </template>

            <template v-else-if="action === 'password'">
                <header><span>ACCOUNT SECURITY</span><h2>修改登录密码</h2><p>密码至少 6 位，支持字母和数字，不可为纯数字。</p></header>
                <van-form :show-error-message="false" @submit="submitPassword" @failed="handleFormFailed">
                    <van-field v-model="passwordForm.login_password" type="password" label="新密码" placeholder="请输入新密码" clearable :rules="passwordRules" />
                    <van-field v-model="passwordForm.re_password" type="password" label="确认密码" placeholder="请再次输入新密码" clearable :rules="confirmPasswordRules" />
                    <van-field v-model="passwordForm.phone_verify" type="tel" maxlength="4" label="短信验证码" placeholder="请输入 4 位验证码" clearable :rules="codeRules">
                        <template #button><van-button size="small" type="info" native-type="button" :disabled="profileCountdown > 0" :loading="codeSending" @click="sendProfileCode">{{ profileCountdown > 0 ? profileCountdown + 's' : '发送验证码' }}</van-button></template>
                    </van-field>
                    <div class="mobile-account-submit"><van-button block round type="info" native-type="submit" :loading="submitting">确认修改</van-button></div>
                </van-form>
            </template>

            <template v-else-if="action === 'phone'">
                <header><span>PHONE SECURITY</span><h2>{{ userData.phone ? '更换手机号' : '绑定手机号' }}</h2><p>为保护账号权益，请按步骤完成手机号验证。</p></header>

                <div v-if="phoneQuota" class="mobile-account-quota-tip">
                    <van-icon name="info-o" />
                    <span>{{ phoneQuota.message }}</span>
                </div>

                <van-steps :active="phoneStep" active-color="#2478f2">
                    <van-step>{{ userData.phone ? '验证旧手机' : '输入手机号' }}</van-step>
                    <van-step>输入新手机</van-step>
                    <van-step>验证新手机</van-step>
                    <van-step>绑定完成</van-step>
                </van-steps>

                <div v-if="phoneStep === 0" class="mobile-account-phone-step">
                    <div class="mobile-account-phone-tip"><van-icon name="shield-o" /><span>验证码将发送至</span><strong>{{ maskedPhone }}</strong></div>
                    <van-form :show-error-message="false" @submit="verifyOldPhone" @failed="handleFormFailed">
                        <van-field v-model="phoneForm.oldCode" type="tel" maxlength="4" label="短信验证码" placeholder="请输入 4 位验证码" clearable :rules="codeRules">
                            <template #button><van-button size="small" type="info" native-type="button" :disabled="oldPhoneCountdown > 0" :loading="codeSending" @click="sendOldPhoneCode">{{ oldPhoneCountdown > 0 ? oldPhoneCountdown + 's' : '发送验证码' }}</van-button></template>
                        </van-field>
                        <div class="mobile-account-submit"><van-button block round type="info" native-type="submit" :loading="submitting">验证旧手机</van-button></div>
                    </van-form>
                </div>

                <div v-else-if="phoneStep === 1" class="mobile-account-phone-step">
                    <van-form :show-error-message="false" @submit="sendNewPhoneCode" @failed="handleFormFailed">
                        <van-field v-model.trim="phoneForm.newPhone" type="tel" maxlength="11" label="新手机号" placeholder="请输入 11 位手机号" clearable :rules="phoneRules" />
                        <div class="mobile-account-submit"><van-button block round type="info" native-type="submit" :loading="submitting">获取新手机验证码</van-button></div>
                    </van-form>
                </div>

                <div v-else-if="phoneStep === 2" class="mobile-account-phone-step">
                    <div class="mobile-account-phone-tip"><van-icon name="phone-o" /><span>验证码已发送至</span><strong>{{ maskPhone(phoneForm.newPhone) }}</strong></div>
                    <van-form :show-error-message="false" @submit="verifyNewPhone" @failed="handleFormFailed">
                        <van-field v-model="phoneForm.newCode" type="tel" maxlength="4" label="短信验证码" placeholder="请输入 4 位验证码" clearable :rules="codeRules">
                            <template #button><van-button size="small" type="info" native-type="button" :disabled="newPhoneCountdown > 0" :loading="codeSending" @click="sendNewPhoneCode">{{ newPhoneCountdown > 0 ? newPhoneCountdown + 's' : '重新发送' }}</van-button></template>
                        </van-field>
                        <div class="mobile-account-submit"><van-button block round type="info" native-type="submit" :loading="submitting">确认绑定</van-button></div>
                    </van-form>
                </div>

                <div v-else class="mobile-account-phone-done">
                    <van-icon name="checked" />
                    <h3>绑定完成</h3>
                    <p>你现在可以使用新手机号登录。</p>
                    <van-button block round type="info" @click="visible = false">我知道了</van-button>
                </div>
            </template>
        </section>
    </van-popup>
</template>

<script>
export default {
    name: 'MobileAccountSettings',
    props: {
        value: Boolean,
        action: {type: String, default: ''},
        userData: {type: Object, default: () => ({})},
        phoneQuota: {type: Object, default: null},
    },
    data() {
        return {
            submitting: false,
            codeSending: false,
            profileCountdown: 0,
            oldPhoneCountdown: 0,
            newPhoneCountdown: 0,
            timers: [],
            phoneStep: 0,
            nicknameForm: {nickname: '', phone_verify: ''},
            passwordForm: {login_password: '', re_password: '', phone_verify: ''},
            phoneForm: {oldCode: '', newPhone: '', newCode: ''},
            codeRules: [{required: true, pattern: /^\d{4}$/, message: '请输入 4 位数字验证码'}],
            passwordRules: [{validator: value => /^(?!\d+$)[A-Za-z0-9]{6,}$/.test(value), message: '至少 6 位字母或数字，不可为纯数字'}],
            confirmPasswordRules: [{validator: value => value === this.passwordForm.login_password, message: '两次输入的密码不一致'}],
            phoneRules: [{validator: value => /^1\d{10}$/.test(value), message: '请输入正确的 11 位手机号'}],
        };
    },
    computed: {
        visible: {
            get() { return this.value; },
            set(value) { this.$emit('input', value); },
        },
        maskedPhone() { return this.maskPhone(this.userData.phone); },
    },
    watch: {
        value(opened) {
            if (opened) this.reset();
        },
    },
    methods: {
        handleFormFailed(errorInfo) {
            const firstError = errorInfo && errorInfo.errors && errorInfo.errors[0];
            if (firstError && firstError.message) this.$toast(firstError.message);
        },
        reset() {
            this.timers.forEach(timer => clearInterval(timer));
            this.timers = [];
            this.submitting = false;
            this.codeSending = false;
            this.profileCountdown = 0;
            this.oldPhoneCountdown = 0;
            this.newPhoneCountdown = 0;
            this.phoneStep = this.userData.phone ? 0 : 1;
            this.nicknameForm = {nickname: '', phone_verify: ''};
            this.passwordForm = {login_password: '', re_password: '', phone_verify: ''};
            this.phoneForm = {oldCode: '', newPhone: '', newCode: ''};
        },
        startCountdown(field) {
            this[field] = 120;
            const timer = setInterval(() => {
                this[field]--;
                if (this[field] <= 0) clearInterval(timer);
            }, 1000);
            this.timers.push(timer);
        },
        sendCode(url, field, data = {}) {
            if (this.codeSending || this[field] > 0) return Promise.resolve();
            this.codeSending = true;
            return this.$bbs.post(url, data, {need: {error: true}}).then(() => {
                this.startCountdown(field);
                this.$toast.success('验证码已发送');
            }).finally(() => { this.codeSending = false; });
        },
        sendProfileCode() { return this.sendCode('/bbs/user/pco', 'profileCountdown'); },
        submitNickname() {
            this.submitting = true;
            this.$bbs.put('/bbs/user/profile', this.nicknameForm, {need: {error: true}}).then(response => {
                const nickname = response.data && response.data.nickname || this.nicknameForm.nickname;
                this.$emit('updated', {nickname});
                this.$toast.success('昵称已更新');
                this.visible = false;
            }).finally(() => { this.submitting = false; });
        },
        submitPassword() {
            this.submitting = true;
            this.$bbs.put('/bbs/user/password', this.passwordForm, {need: {error: true}}).then(() => {
                this.$toast.success('密码修改成功');
                this.visible = false;
            }).finally(() => { this.submitting = false; });
        },
        sendOldPhoneCode() { return this.sendCode('/bbs/user/unPhone', 'oldPhoneCountdown'); },
        verifyOldPhone() {
            this.submitting = true;
            this.$bbs.put('/bbs/user/unPhone', {code: this.phoneForm.oldCode}, {need: {error: true}}).then(() => {
                this.phoneStep = 1;
            }).finally(() => { this.submitting = false; });
        },
        sendNewPhoneCode() {
            if (this.submitting || this.codeSending) return Promise.resolve();
            this.submitting = true;
            this.codeSending = true;
            return this.$bbs.post('/bbs/user/cPhone', {phone: this.phoneForm.newPhone}, {need: {error: true}}).then(() => {
                this.phoneStep = 2;
                this.startCountdown('newPhoneCountdown');
                this.$toast.success('验证码已发送');
            }).catch(error => {
                if (error.data && Number(error.data.code) === 1410) {
                    this.$dialog.alert({title: '新手机号无法绑定', message: '该手机号存在未到期权益或账号正在注销中，请联系 BIGEMAP 客服确认。'});
                }
            }).finally(() => {
                this.submitting = false;
                this.codeSending = false;
            });
        },
        verifyNewPhone() {
            this.submitting = true;
            this.$bbs.post('/bbs/user/vPhone', {code: this.phoneForm.newCode, phone: this.phoneForm.newPhone}, {need: {error: true}}).then(() => {
                this.phoneStep = 3;
                this.$emit('updated', {phone: this.phoneForm.newPhone});
            }).finally(() => { this.submitting = false; });
        },
        maskPhone(phone) {
            const text = String(phone || '');
            return text ? text.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') : '未绑定';
        },
    },
    beforeDestroy() { this.reset(); },
};
</script>

<style scoped lang="less">
.mobile-account-panel { min-height: 380px; padding: 27px 14px 22px; box-sizing: border-box; }
.mobile-account-panel header { padding: 0 5px 20px; }
.mobile-account-panel header span { color: #4e8add; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; }
.mobile-account-panel header h2 { margin: 5px 0 6px; color: #1e293a; font-size: 21px; }
.mobile-account-panel header p { margin: 0; color: #929ba9; font-size: 12px; line-height: 1.55; }
.mobile-account-panel /deep/ .van-field { padding-top: 13px; padding-bottom: 13px; }
.mobile-account-panel /deep/ .van-field__label { width: 88px; flex: 0 0 88px; color: #667287; line-height: 32px; }
.mobile-account-panel /deep/ .van-field__value { min-width: 0; }
.mobile-account-panel /deep/ .van-field__body { min-height: 32px; align-items: center; }
.mobile-account-panel /deep/ .van-field__control { line-height: 24px; }
.mobile-account-panel /deep/ .van-field__error-message { display: none; }
.mobile-account-panel /deep/ .van-field__button { height: 32px; margin-left: 8px; flex: 0 0 84px; }
.mobile-account-panel /deep/ .van-field__button .van-button { width: 84px; height: 32px; padding: 0 5px; color: #2478f2; background: #edf5ff; border-color: #c6ddfb; }
.mobile-account-panel /deep/ .van-steps { margin: 2px -8px 10px; }
.mobile-account-panel /deep/ .van-step__title { font-size: 10px; }
.mobile-account-submit { padding: 23px 9px 3px; }
.mobile-account-submit /deep/ .van-button { height: 44px; background: linear-gradient(135deg,#398df4,#1c67d1); border: 0; box-shadow: 0 7px 16px rgba(38,116,217,.2); }
.mobile-account-phone-step { padding-top: 10px; }
.mobile-account-quota-tip { display: flex; margin: 0 5px 12px; padding: 10px 12px; align-items: center; color: #66758a; font-size: 11px; line-height: 1.5; background: #f2f7fd; border-radius: 9px; gap: 7px; }
.mobile-account-quota-tip .van-icon { color: #347fdc; font-size: 16px; }
.mobile-account-phone-tip { display: flex; margin: 0 5px 14px; padding: 13px; align-items: center; color: #718096; font-size: 12px; background: #f4f8fd; border: 1px solid #e2ebf6; border-radius: 11px; gap: 7px; }
.mobile-account-phone-tip .van-icon { color: #347fdc; font-size: 18px; }
.mobile-account-phone-tip strong { margin-left: auto; color: #34445b; font-size: 13px; }
.mobile-account-phone-done { padding: 24px 14px 4px; text-align: center; }
.mobile-account-phone-done > .van-icon { color: #25a46c; font-size: 58px; }
.mobile-account-phone-done h3 { margin: 11px 0 6px; font-size: 20px; }
.mobile-account-phone-done p { margin: 0 0 25px; color: #929ba9; font-size: 13px; }
</style>
